/* ============================================================
   PlayShop — Cart Drawer (vanilla JS, ZÉRO Horizon morph)
   ============================================================
   API publique sur window.PlayShopCart :
     - open()            : ouvre le drawer + refresh content
     - close()           : ferme le drawer
     - refresh()         : refetch /cart.js + re-render
     - add(items)        : add to cart + open drawer
     - addPermalink(url) : GET un permalink Shopify (add + discount) + refresh
     - getCart()         : Promise<Cart>

   Events globaux dispatchés sur document :
     - 'ps:cart:updated' { cart }  → après chaque update du cart
     - 'ps:cart:opened'
     - 'ps:cart:closed'
============================================================ */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: '[data-ps-cart-drawer]',
    content: '[data-ps-cart-content]',
    footer: '[data-ps-cart-footer]',
    tier: '[data-ps-cart-tier]',
    countText: '[data-ps-cart-count-text]',
    clear: '[data-ps-cart-clear]',
    total: '[data-ps-cart-total]',
    closeBtns: '[data-ps-cart-close]',
    trigger: '[data-ps-cart-trigger]',
    countBubble: '[data-ps-cart-count]',
  };

  const MONEY_FORMAT = "{{amount_with_comma_separator}} €";

  function formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return euros + ' €';
  }

  function el(selector, root) {
    return (root || document).querySelector(selector);
  }
  function els(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  // ─── Fetch cart state ───
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  // ─── Plafond de quantité par commande ───
  // Un produit « limité » porte la propriété de ligne cachée `_max_qty` (injectée
  // par le formulaire produit). Si une ligne dépasse ce max, on la reclampe direct
  // dans le panier (double-clic, +/- manuel…). Cap SOFT côté client ; le cap DUR
  // au checkout (requête forgée) reste la Shopify Function fast-follow.
  function lineMax(item) {
    const m = item && item.properties && item.properties._max_qty;
    const n = m ? parseInt(m, 10) : 0;
    return n > 0 ? n : 0;
  }
  let limitToastAt = 0;
  function showLimitToast(max) {
    const now = Date.now();
    if (now - limitToastAt < 1500) return; // anti-spam
    limitToastAt = now;
    let t = document.getElementById('ps-cart-limit-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'ps-cart-limit-toast';
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;background:#141428;color:#fff;border:1.5px solid #FF8A00;border-radius:10px;padding:.7rem 1rem;font-size:.85rem;font-weight:700;box-shadow:0 6px 24px #0007;max-width:90vw;text-align:center;opacity:0;transition:opacity .2s;';
      document.body.appendChild(t);
    }
    t.textContent = 'Limité à ' + (max || 1) + ' exemplaire' + ((max || 1) > 1 ? 's' : '') + ' par commande.';
    requestAnimationFrame(() => { t.style.opacity = '1'; });
    clearTimeout(t._hide);
    t._hide = setTimeout(() => { t.style.opacity = '0'; }, 2600);
  }
  // Reclampe toutes les lignes qui dépassent leur plafond. Retourne le cart mis à
  // jour si une correction a eu lieu, sinon null.
  async function enforceLimits(cart) {
    const overs = (cart && cart.items ? cart.items : []).filter((it) => {
      const max = lineMax(it);
      return max && it.quantity > max;
    });
    if (!overs.length) return null;
    let latest = cart;
    let lastMax = 1;
    for (const it of overs) {
      lastMax = lineMax(it);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: lastMax }),
        });
        latest = await res.json();
      } catch (e) {
        console.error('[PlayShop Cart] enforceLimits error:', e);
      }
    }
    showLimitToast(lastMax);
    return latest;
  }

  // ─── Render line items HTML ───
  function renderLine(item) {
    const imageUrl = item.image
      ? item.image.replace(/(\.[a-z]+)(?=\?|$)/i, '_60x60$1')
      : null;
    const variantTitle = item.variant_options && item.variant_options.filter(o => o !== 'Default Title').join(' / ');
    // 17/10/2026 : on ne barre QUE si le prix de la ligne a vraiment baisse. Un code COMMANDE (VIP5...)
    // compte dans total_discount mais pas dans final_line_price : on barrait 12,90 pour afficher 12,90.
    const lineDiscount = item.original_line_price > item.final_line_price
      ? '<span class="ps-cart-line__price-original">' + formatMoney(item.original_line_price) + '</span>'
      : '';

    return `
      <div class="ps-cart-line" data-line-key="${item.key}" data-variant-id="${item.variant_id}">
        ${imageUrl ? `<img class="ps-cart-line__image" src="${imageUrl}" alt="${item.product_title}" loading="lazy">` : '<div class="ps-cart-line__image"></div>'}
        <div>
          <a href="${item.url}" class="ps-cart-line__title">${item.product_title}</a>
          ${variantTitle ? `<div class="ps-cart-line__variant">${variantTitle}</div>` : ''}
          <div class="ps-cart-line__qty">
            <button type="button" class="ps-cart-line__qty-btn" data-action="dec" aria-label="Diminuer">−</button>
            <span class="ps-cart-line__qty-value">${item.quantity}</span>
            <button type="button" class="ps-cart-line__qty-btn" data-action="inc" aria-label="Augmenter">+</button>
            <button type="button" class="ps-cart-line__remove" data-action="remove" aria-label="Supprimer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </div>
        <div class="ps-cart-line__price">
          ${lineDiscount}
          ${formatMoney(item.final_line_price)}
        </div>
      </div>
    `;
  }

  // ─── Render full cart content ───
  function renderCart(cart) {
    const drawer = el(SELECTORS.drawer);
    if (!drawer) return;

    const content = el(SELECTORS.content, drawer);
    const footer = el(SELECTORS.footer, drawer);
    const tier = el(SELECTORS.tier, drawer);
    const countText = el(SELECTORS.countText, drawer);
    const total = el(SELECTORS.total, drawer);

    // Update count text in header
    if (countText) countText.textContent = cart.item_count;

    // Bouton "Vider" : visible seulement si le panier a des articles
    const clearBtn = el(SELECTORS.clear, drawer);
    if (clearBtn) clearBtn.hidden = cart.item_count === 0;

    // Update header cart bubble (global)
    els(SELECTORS.countBubble).forEach(b => {
      b.textContent = cart.item_count;
      b.classList.toggle('is-empty', cart.item_count === 0);
    });

    // Le footer reste visible meme cart vide : l'user peut voir une reduc
    // de frais de port deja appliquee (ou en saisir une) avant d'ajouter
    // des produits. Seuls le tier card + le bouton checkout changent.
    const checkoutBtn = drawer.querySelector('[data-ps-checkout-btn]');
    const cartNote = drawer.querySelector('[data-ps-cart-note]');

    if (cart.item_count === 0) {
      content.innerHTML = `
        <div class="ps-cart-drawer__empty">
          <p>Ton panier est vide</p>
        </div>
      `;
      footer.hidden = false;
      // Tier card visible meme cart vide : on rappelle le cashback que
      // l'user gagnera quand il aura des produits.
      if (tier) {
        tier.hidden = false;
        const tierCard = tier.querySelector('.ps-tier-card[data-ps-tier-card="cart"]');
        if (tierCard) {
          tierCard.dataset.psPriceCents = '0';
          if (window.PlayShop && window.PlayShop.TierCard && window.PlayShop.TierCard.update) {
            window.PlayShop.TierCard.update('cart', 0);
          }
        }
      }
      if (total) total.textContent = formatMoney(0);
      if (checkoutBtn) {
        checkoutBtn.textContent = 'Continuer mes achats';
        checkoutBtn.setAttribute('href', '#');
        checkoutBtn.dataset.psCheckoutEmpty = '1';
      }
      if (cartNote) cartNote.hidden = true;
      // Panier vide = repart de zéro : on oublie les codes retirés (sinon un code
      // retiré resterait exclu après avoir re-rempli le panier, même token).
      resetAutodiscountMemory();
      // Permet de voir une reduc shipping_free deja appliquee meme sans produit.
      renderDiscount(cart);
      return;
    }

    content.innerHTML = cart.items.map(renderLine).join('');
    if (total) total.textContent = formatMoney(cart.total_price);
    footer.hidden = false;
    if (checkoutBtn) {
      checkoutBtn.textContent = 'Passer commande →';
      checkoutBtn.setAttribute('href', '/checkout');
      delete checkoutBtn.dataset.psCheckoutEmpty;
    }
    if (cartNote) cartNote.hidden = false;

    // Show applied discount(s) if any
    renderDiscount(cart);

    // Show + update tier card (loyalty preview)
    if (tier) {
      tier.hidden = false;
      const tierCard = tier.querySelector('.ps-tier-card[data-ps-tier-card="cart"]');
      if (tierCard) {
        tierCard.dataset.psPriceCents = String(cart.total_price);
        // Trigger tier card update via global API
        if (window.PlayShop && window.PlayShop.TierCard && window.PlayShop.TierCard.update) {
          window.PlayShop.TierCard.update('cart', cart.total_price);
        }
      }
    }
  }

  // ─── Render applied discount ───
  function renderDiscount(cart) {
    const drawer = el(SELECTORS.drawer);
    if (!drawer) return;
    const applied = drawer.querySelector('[data-ps-discount-applied]');
    const error = drawer.querySelector('[data-ps-discount-error]');
    const form = drawer.querySelector('[data-ps-discount-form]');
    const input = drawer.querySelector('[data-ps-discount-input]');

    // Reset states
    if (error) error.hidden = true;

    // Panier vide : on n'affiche AUCUNE reduc (fini le « Frais de port offerts applique »
    // fantome sur un panier a 0 article -> c'etait l'asymetrie deroutante quand on
    // supprimait le dernier produit). On garde juste le champ code visible.
    if (cart && cart.item_count === 0) {
      if (applied) { applied.hidden = true; applied.innerHTML = ''; }
      if (form) form.hidden = false;
      return;
    }

    // Read discount applications from cart.js response.
    // NB : free shipping codes n'apparaissent PAS dans cart.js (Shopify les
    // applique au calcul shipping = au checkout uniquement). On les pioche
    // dans sessionStorage (set par le submit handler) pour rendre l'UX claire :
    // "✓ XXX appliqué — Frais offerts au checkout".
    const discounts = (cart.cart_level_discount_applications || [])
      .concat((cart.discount_applications || []).filter(d => d.target_selection === 'all'));

    let pendingShippingCode = null;
    let pendingShippingLabel = null;
    let pendingTape = false; // code TAPE par le client (chaine nue), pas un code auto {code,label}
    try {
      const raw = sessionStorage.getItem('ps_applied_shipping_code');
      if (raw) {
        pendingTape = raw.charAt(0) !== '{';
        // Support 2 formats : { code, label } JSON (v11+) ou string nue (legacy)
        if (raw.charAt(0) === '{') {
          const parsed = JSON.parse(raw);
          pendingShippingCode = parsed.code;
          pendingShippingLabel = parsed.label || parsed.code;
        } else {
          pendingShippingCode = raw;
          pendingShippingLabel = raw;
        }
      }
    } catch (e) {}

    // 17/10/2026 : le code est mis en sessionStorage AVANT la reponse de Shopify (submit). Un code
    // invente s'affichait donc « ✓ CODE applique ». Shopify GARDE un code refuse dans le panier avec
    // applicable:false (mesure : /discount/TESTFAUX1710 -> discount_codes [{code, applicable:false}]).
    // Un code de port valable, lui, est applicable:true meme s'il n'a aucune ligne de reduction
    // (logique de cart-discount.js d'Horizon). On n'affiche donc « applique » QUE si applicable:true.
    if (pendingShippingCode && Array.isArray(cart.discount_codes)) {
      const cible = String(pendingShippingCode).toUpperCase();
      const entree = cart.discount_codes.find(d => d && String(d.code || '').toUpperCase() === cible);
      if (!entree || entree.applicable !== true) {
        if (pendingTape) {
          // Code tape refuse : on l'oublie et on le DIT au client.
          try { sessionStorage.removeItem('ps_applied_shipping_code'); } catch (e) {}
          if (error) {
            error.textContent = 'Le code ' + pendingShippingCode + " n'est pas valable pour ce panier.";
            error.hidden = false;
          }
          pendingShippingCode = null;
          pendingShippingLabel = null;
        } else if (entree && entree.applicable === false) {
          // Code auto (fidelite) refuse par Shopify : on ne l'affiche pas « applique »,
          // sans toucher a sa memoire (sinon l'auto-application le reposerait en boucle).
          pendingShippingCode = null;
          pendingShippingLabel = null;
        }
      }
    }

    if (!discounts.length && !pendingShippingCode) {
      // No discount applied : hide applied box, keep form visible
      if (applied) {
        applied.hidden = true;
        applied.innerHTML = '';
      }
      if (form) form.hidden = false;
      return;
    }

    // Discount(s) applied : show summary + remove button. Hide form input.
    if (applied) {
      const cartDiscountsHtml = discounts.map(d => {
        const code = d.title || d.code || 'Réduction';
        const savings = d.total_allocated_amount || cart.total_discount || 0;
        return `
          <div class="ps-cart-drawer__discount-item">
            <span>✓ <strong>${code}</strong> appliqué - ${formatMoney(savings)}</span>
            <button type="button" class="ps-cart-drawer__discount-remove" data-ps-discount-remove="${code}" aria-label="Retirer le code">×</button>
          </div>
        `;
      }).join('');
      // Si la sessionStorage tracke un code shipping (pas visible dans cart.js
      // car shipping discount ne s'applique qu'au calcul checkout) et qu'aucun
      // discount cart-level n'est deja affiche pour ce code, on l'ajoute.
      let shippingHtml = '';
      const alreadyShownCodes = discounts.map(d => (d.title || d.code || '').toUpperCase());
      if (pendingShippingCode && !alreadyShownCodes.includes(pendingShippingCode.toUpperCase())) {
        shippingHtml = `
          <div class="ps-cart-drawer__discount-item">
            <span>✓ <strong>${pendingShippingLabel || pendingShippingCode}</strong> appliqué</span>
            <button type="button" class="ps-cart-drawer__discount-remove" data-ps-discount-remove="${pendingShippingCode}" data-ps-discount-shipping="1" aria-label="Retirer le code">×</button>
          </div>
        `;
      }
      applied.innerHTML = cartDiscountsHtml + shippingHtml;
      applied.hidden = false;
    }
    if (form) form.hidden = true;
    if (input) input.value = '';
  }

  // ─── Public API ───
  let lastCart = null;
  // Flag anti-recursion : refresh() dispatche 'cart:update', et la
  // compat listener appelle refresh() sur cet event -> boucle infinie.
  // Le flag previens le re-entry pendant qu'un refresh est en cours.
  let isRefreshing = false;

  // ─── Auto-application du meilleur code % du client (Venom #19 : voir la réduc direct) ───
  // À l'ouverture du panier d'un client connecté, si AUCUNE réduc n'est appliquée et qu'il
  // n'a pas retiré de code sur CE panier (opt-out par cart token), on applique en tâche de
  // fond son meilleur code % (le plus avantageux ; on n'empile jamais 2 %). Application via
  // fetch('/discount/CODE?redirect=/cart.js') = server-side, SANS reload. Shopify valide le
  // code (refuse expiré/invalide). La croix × existante le retire, et retirer pose l'opt-out
  // → pas de ré-application sur ce panier (mais oui sur la commande suivante = nouveau token).
  // Cache des codes du client (1 seul fetch dashboard-data par chargement, reutilise a chaque
  // changement de panier). Promise memoisee -> pas de rafale de requetes sur les +/-/suppr.
  let codesCachePromise = null;
  function fetchClientCodes(url, key, cid) {
    if (!codesCachePromise) {
      codesCachePromise = fetch(url + '/functions/v1/dashboard-data?cid=' + encodeURIComponent(cid), {
        headers: { 'apikey': key, 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
      }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    }
    return codesCachePromise;
  }
  let autoApplyInFlight = false; // garde re-entrance (pas un « une seule fois » : on resync a chaque refresh)
  function optoutKey(cart) { return 'ps_autodiscount_optout_' + ((cart && cart.token) || 'x'); }
  // Opt-out PAR CODE : quand le client retire un code, on le note ici pour qu'il ne se
  // ré-applique PAS, tout en laissant l'autre code (gardé) se ré-appliquer.
  // 🔑 UNE SEULE clé (PAS par cart token) : `/discount/none` RÉGÉNÈRE le cart token de
  //    Shopify, donc une clé token-scopée serait relue sous un token différent juste après
  //    le retrait -> liste vide -> le code retiré revenait. Clé unique = robuste. Remis à
  //    zéro quand le panier se vide (resetAutodiscountMemory).
  function removedKey() { return 'ps_autodiscount_removed_all'; }
  function lireRetires() {
    try { var a = JSON.parse(sessionStorage.getItem(removedKey()) || '[]'); return Array.isArray(a) ? a.map(function (c) { return String(c).toUpperCase(); }) : []; }
    catch (e) { return []; }
  }
  function noterRetire(code) {
    try {
      var up = String(code || '').toUpperCase(); if (!up) return;
      var a = lireRetires(); if (a.indexOf(up) === -1) { a.push(up); sessionStorage.setItem(removedKey(), JSON.stringify(a)); }
    } catch (e) {}
  }
  // Panier vidé = on repart de zéro : on oublie TOUS les codes retirés / opt-out / gardes
  // (le cart token ne change pas quand on vide, donc sans ça un code retiré resterait exclu
  // même après avoir re-rempli le panier). Les 2 codes se re-proposent alors normalement.
  function resetAutodiscountMemory() {
    try {
      var aSupprimer = [];
      for (var i = 0; i < sessionStorage.length; i++) {
        var k = sessionStorage.key(i);
        if (k && (k.indexOf('ps_autodiscount_removed_') === 0
               || k.indexOf('ps_autodiscount_optout_') === 0
               || k.indexOf('ps_cartpage_autodiscount_') === 0)) aSupprimer.push(k);
      }
      aSupprimer.forEach(function (k) { sessionStorage.removeItem(k); });
      sessionStorage.removeItem('ps_applied_shipping_code');
    } catch (e) {}
  }
  async function maybeAutoApplyDiscount(cart) {
    if (autoApplyInFlight) return;
    if (!cart || !cart.items || !cart.items.length) return;
    const card = document.querySelector('.ps-tier-card[data-ps-customer-id]');
    if (!card) return; // pas connecté
    const cid = card.dataset.psCustomerId, url = card.dataset.psSupabaseUrl, key = card.dataset.psSupabaseKey;
    if (!cid || !url || !key) return;
    try { if (sessionStorage.getItem(optoutKey(cart)) === '1') return; } catch (e) {}

    // Etat courant, chaque code JUGE INDEPENDAMMENT :
    //  - code COMMANDE (%) : visible dans cart.js (cart_level_discount_applications / target 'all').
    //  - code LIVRAISON : n'apparait PAS dans cart.js (Shopify l'applique au calcul shipping =
    //    au checkout) -> on le tracke via sessionStorage['ps_applied_shipping_code'].
    // On ne s'arrete PLUS des qu'UN code est la : si le port manque alors que la commande %
    // est deja posee (ou l'inverse), on complete le manquant. C'est ce qui rend l'etat stable
    // quand on ajoute/retire des produits (ex : vider puis re-remplir droppait un des 2).
    // ⛔ 06/09/2026 — NE PAS juger « le code commande est pose » sur la simple
    //    PRESENCE d'une reduction. Un code RECOMPENSE (booster offert) est une
    //    reduction PRODUIT : il occupe une application de reduction sans etre le
    //    code du client. Le drawer croyait donc VIP5 pose alors qu'il manquait
    //    (vecu : booster offert + port offert appliques, VIP5 absent du panier).
    //    On juge par IDENTITE DE CODE. Meme famille que le bug du 21/08 ou un
    //    port offert « 100% » ecrasait VIP5 : ne jamais deduire un code d'un montant.
    const codesAppliques = (cart.discount_applications || []).map(function (d) {
      return String((d && (d.title || d.code)) || '').toUpperCase();
    });
    let shipApplied = false;
    try { shipApplied = !!sessionStorage.getItem('ps_applied_shipping_code'); } catch (e) {}

    autoApplyInFlight = true;
    try {
      const data = await fetchClientCodes(url, key, cid);
      // v2 : code COMMANDE (meilleur %) ET code LIVRAISON se cumulent (combinesWith Shopify).
      // dashboard-data a deja filtre (non utilises, non expires).
      const codes = (data && data.discount_codes) || [];
      // On EXCLUT les codes que le client a explicitement retirés sur ce panier.
      const retires = lireRetires();
      const orderCodes = codes.filter(function (c) { return c && c.code && c.applies_to !== 'shipping' && retires.indexOf(c.code.toUpperCase()) === -1; });
      const shipCodes  = codes.filter(function (c) { return c && c.code && c.applies_to === 'shipping' && retires.indexOf(c.code.toUpperCase()) === -1; });
      orderCodes.sort(function (a, b) { return Number(b.value || 0) - Number(a.value || 0); });
      // On ne (re)pose QUE le(s) code(s) manquant(s).
      const aAppliquer = [];
      const codeCommande = orderCodes[0] ? String(orderCodes[0].code) : null;
      const commandeDejaPosee = !!codeCommande && codesAppliques.indexOf(codeCommande.toUpperCase()) !== -1;
      if (!commandeDejaPosee && codeCommande) aAppliquer.push(codeCommande);
      if (!shipApplied && shipCodes[0])  aAppliquer.push(shipCodes[0].code);
      if (!aAppliquer.length) return;
      // Chaine : /discount/A?redirect=/discount/B?redirect=/cart.js -> les 2 codes restent en session.
      const chaine = aAppliquer.reduceRight(function (next, c) {
        return '/discount/' + encodeURIComponent(c) + '?redirect=' + encodeURIComponent(next);
      }, '/cart.js');
      await fetch(chaine, { method: 'GET', credentials: 'same-origin', redirect: 'follow' });
      // Memorise le code livraison pour l'afficher « applique » (invisible dans cart.js).
      if (!shipApplied && shipCodes[0]) { try { sessionStorage.setItem('ps_applied_shipping_code', JSON.stringify({ code: shipCodes[0].code, label: 'Frais de port offerts' })); } catch (e) {} }
      const fresh = await fetchCart();
      lastCart = fresh;
      renderCart(fresh);
      document.dispatchEvent(new CustomEvent('ps:cart:updated', { detail: { cart: fresh } }));
    } catch (e) { /* silencieux : ne jamais bloquer le panier pour un auto-code */ }
    finally { autoApplyInFlight = false; }
  }

  async function refresh() {
    if (isRefreshing) return;
    isRefreshing = true;
    try {
      let cart = await fetchCart();
      const capped = await enforceLimits(cart);
      if (capped) cart = capped;
      lastCart = cart;
      renderCart(cart);
      document.dispatchEvent(new CustomEvent('ps:cart:updated', { detail: { cart } }));
      // Dispatch aussi 'cart:update' pour compat avec les listeners qui
      // l'utilisent (playshop-tier-card pour le refund auto des rewards,
      // playshop-product-actions pour le quick-add). Detail.resource = cart
      // pour matcher le format Horizon attendu par certains listeners.
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart, resource: cart } }));
      // Auto-application du meilleur code % (fire-and-forget, auto-gardé, jamais bloquant).
      maybeAutoApplyDiscount(cart);
    } catch (e) {
      console.error('[PlayShop Cart] refresh error:', e);
    } finally {
      isRefreshing = false;
    }
  }

  function open() {
    const drawer = el(SELECTORS.drawer);
    if (!drawer) return;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    refresh();
    document.dispatchEvent(new CustomEvent('ps:cart:opened'));
  }

  function close() {
    const drawer = el(SELECTORS.drawer);
    if (!drawer) return;
    drawer.hidden = true;
    document.body.style.overflow = '';
    document.dispatchEvent(new CustomEvent('ps:cart:closed'));
  }

  async function changeQty(lineKey, newQty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty }),
      });
      let cart = await res.json();
      const capped = await enforceLimits(cart);
      if (capped) cart = capped;
      lastCart = cart;
      renderCart(cart);
      document.dispatchEvent(new CustomEvent('ps:cart:updated', { detail: { cart } }));
      // Dispatch aussi 'cart:update' pour compat avec les listeners qui
      // l'utilisent (playshop-tier-card pour le refund auto des rewards,
      // playshop-product-actions pour le quick-add). Detail.resource = cart
      // pour matcher le format Horizon attendu par certains listeners.
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart, resource: cart } }));
      // Si la page panier est ouverte derrière le drawer, on la resynchronise (server-rendered) :
      // elle rechargera avec le bon contenu ET ré-appliquera les codes voulus au chargement.
      if (window.location.pathname === '/cart') { window.location.reload(); return; }
      // Resync des codes apres un +/- ou une suppression : si un des 2 codes a saute
      // (ex Shopify drop la reduc commande quand le panier se vide puis se re-remplit),
      // on repose le manquant. Idempotent (return direct si les 2 sont deja la).
      maybeAutoApplyDiscount(cart);
    } catch (e) {
      console.error('[PlayShop Cart] changeQty error:', e);
    }
  }

  async function add(items) {
    if (!Array.isArray(items)) items = [items];
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (data.status && data.status >= 400) throw new Error(data.message || 'Erreur ajout panier');
      await refresh();
      open();
      return data;
    } catch (e) {
      console.error('[PlayShop Cart] add error:', e);
      throw e;
    }
  }

  async function addPermalink(url) {
    try {
      await fetch(url, { method: 'GET', credentials: 'same-origin', redirect: 'follow' });
      await refresh();
      open();
    } catch (e) {
      console.error('[PlayShop Cart] addPermalink error:', e);
    }
  }

  // ─── Modal de confirmation custom (fallback window.confirm) ───
  function psConfirm(message, okLabel) {
    return new Promise((resolve) => {
      const modal = document.querySelector('[data-ps-cart-confirm]');
      if (!modal) { resolve(window.confirm(message)); return; }
      const msgEl = modal.querySelector('[data-ps-cart-confirm-msg]');
      const okBtn = modal.querySelector('[data-ps-cart-confirm-ok]');
      const cancelEls = modal.querySelectorAll('[data-ps-cart-confirm-cancel]');
      if (msgEl && message) msgEl.textContent = message;
      if (okBtn && okLabel) okBtn.textContent = okLabel;
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-open'));
      function cleanup(result) {
        modal.classList.remove('is-open');
        okBtn.removeEventListener('click', onOk);
        cancelEls.forEach((c) => c.removeEventListener('click', onCancel));
        document.removeEventListener('keydown', onKey);
        setTimeout(() => { modal.hidden = true; }, 160);
        resolve(result);
      }
      function onOk() { cleanup(true); }
      function onCancel() { cleanup(false); }
      function onKey(e) { if (e.key === 'Escape') cleanup(false); }
      okBtn.addEventListener('click', onOk);
      cancelEls.forEach((c) => c.addEventListener('click', onCancel));
      document.addEventListener('keydown', onKey);
      if (okBtn) okBtn.focus();
    });
  }

  // ─── Vider le panier (POST /cart/clear.js + refresh) ───
  async function clearCart(btn) {
    if (btn && btn.dataset.busy) return;
    const ok = await psConfirm('Vider tout le panier ?', 'Vider');
    if (!ok) return;
    if (btn) btn.dataset.busy = '1';
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      await refresh();
      // Si la page panier est ouverte derrière le drawer, on la resynchronise (elle est
      // rendue par le serveur, elle ne se met pas à jour toute seule quand on vide ici).
      if (window.location.pathname === '/cart') { window.location.reload(); return; }
    } catch (e) {
      console.error('[PlayShop Cart] clear error:', e);
    } finally {
      if (btn) delete btn.dataset.busy;
    }
  }

  // ─── Wire up triggers + close buttons ───
  function init() {
    // Trigger click → open
    els(SELECTORS.trigger).forEach(t => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });
    // Close buttons + overlay
    els(SELECTORS.closeBtns).forEach(b => {
      b.addEventListener('click', close);
    });
    // Bouton "Vider le panier"
    document.addEventListener('click', (e) => {
      const cb = e.target.closest && e.target.closest('[data-ps-cart-clear]');
      if (!cb) return;
      e.preventDefault();
      clearCart(cb);
    });
    // Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
    // Click sur "Continuer mes achats" en mode cart vide -> close drawer
    // (sinon le <a href="#"> ferait scroll-top + ajouterait # a l'URL).
    document.addEventListener('click', (e) => {
      const cta = e.target.closest('[data-ps-checkout-btn]');
      if (!cta || !cta.dataset.psCheckoutEmpty) return;
      e.preventDefault();
      close();
    });

    // Quantity buttons + remove (event delegation)
    const drawer = el(SELECTORS.drawer);
    if (drawer) {
      drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const line = btn.closest('.ps-cart-line');
        if (!line) return;
        const key = line.dataset.lineKey;
        const action = btn.dataset.action;
        if (action === 'remove') {
          changeQty(key, 0);
        } else if (action === 'inc') {
          const current = parseInt(line.querySelector('.ps-cart-line__qty-value').textContent, 10) || 1;
          changeQty(key, current + 1);
        } else if (action === 'dec') {
          const current = parseInt(line.querySelector('.ps-cart-line__qty-value').textContent, 10) || 1;
          changeQty(key, Math.max(0, current - 1));
        }
      });
    }
    // Initial cart count from server-rendered bubble (don't fetch on every page load,
    // only refresh on demand or after add)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API
  window.PlayShopCart = { open, close, refresh, add, addPermalink, clear: clearCart, getCart: fetchCart };

  // Also listen to 'cart:update' (compat avec d'anciens modules qui dispatchent ça)
  document.addEventListener('cart:update', refresh);

  // ─── Discount code handler ───
  // Submit form -> redirect /discount/CODE?redirect=/cart (Shopify applique
  // le discount au cart server-side puis renvoie sur /cart). Apres reload,
  // le drawer s'ouvre auto (via persistence) ou user re-click le cart icon
  // -> renderDiscount() affichera le code applique.
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-ps-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[data-ps-discount-input]');
    const btn = form.querySelector('[data-ps-discount-btn]');
    const errBox = form.parentNode.querySelector('[data-ps-discount-error]');
    const code = (input.value || '').trim().toUpperCase();
    if (!code) {
      if (errBox) {
        errBox.textContent = 'Entre un code promo';
        errBox.hidden = false;
      }
      return;
    }
    if (btn) {
      btn.dataset.state = 'loading';
      btn.textContent = '…';
    }
    // Open drawer flag so it re-opens after redirect (sessionStorage).
    // Stocke aussi le code pour pouvoir l'afficher en "applied" apres reload
    // (utile pour les shipping codes qui n'apparaissent pas dans cart.js).
    try {
      sessionStorage.setItem('ps_cart_reopen', '1');
      sessionStorage.setItem('ps_applied_shipping_code', code);
    } catch (e) {}
    // Redirect URL : si on est sur /cart, on tente le document.referrer
    // (page d'ou vient l'user) ou fallback "/". Sinon : page courante.
    // -> user ne se retrouve jamais sur /cart sans contexte, toujours
    //    sur une page produit/collection avec le drawer par-dessus.
    let back = window.location.pathname + window.location.search;
    if (window.location.pathname === '/cart') {
      try {
        const ref = document.referrer;
        if (ref && new URL(ref).origin === window.location.origin && !ref.includes('/cart')) {
          back = ref.replace(window.location.origin, '');
        } else {
          back = '/';
        }
      } catch (e) { back = '/'; }
    }
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
  });

  // Discount remove : 2 chemins selon que le code est un REWARD ou pas.
  //  - REWARD code (shipping_free/voucher_universal/product_free) : call
  //    refund-reward-redemption -> efface code Shopify + ledger refund pts
  //    + delete redemption row. Optimistic UI : pts restitues instant + drawer
  //    refresh sans reload. C'est le SEUL moyen pour l'user de recuperer ses
  //    pts pour un shipping/voucher (pas de "remove product from cart" possible).
  //  - PROMO normal (code que l'user a tape manuellement) : ancien comportement
  //    -> redirect /discount/none?redirect=... qui reload la page.
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('[data-ps-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    const code = btn.dataset.psDiscountRemove;

    // (L'opt-out se fait désormais PAR CODE dans le chemin « code promo » ci-dessous —
    //  retirer un code ne bloque plus l'AUTRE. Les rewards ont leur propre chemin.)

    // Check si c'est un code reward tracke
    const anyCard = document.querySelector('.ps-tier-card[data-ps-customer-id]');
    if (anyCard && code) {
      const cid = anyCard.dataset.psCustomerId;
      const supabaseUrl = anyCard.dataset.psSupabaseUrl;
      const supabaseKey = anyCard.dataset.psSupabaseKey;
      const codesKey = 'ps_tracked_reward_codes_' + cid;
      let tracked = null;
      try {
        const all = JSON.parse(localStorage.getItem(codesKey) || '{"codes":[]}');
        const idx = (all.codes || []).findIndex(function (c) { return c.code === code; });
        if (idx >= 0 && supabaseUrl && supabaseKey) {
          tracked = all.codes[idx];
          // Remove du tracking immediatement (optimistic, anti double-call)
          all.codes.splice(idx, 1);
          localStorage.setItem(codesKey, JSON.stringify(all));
        }
      } catch (_) {}

      if (tracked) {
        // OPTIMISTIC UI : update balance + ledger + toast IMMEDIATEMENT
        if (window.psDashboard && typeof window.psDashboard.optimisticAddPoints === 'function') {
          window.psDashboard.optimisticAddPoints(tracked.cost_pts);
          if (typeof window.psDashboard.optimisticAddLedgerEntry === 'function') {
            window.psDashboard.optimisticAddLedgerEntry({
              delta: tracked.cost_pts,
              reason: 'reward_refund',
              metadata: { reward_name: tracked.reward_name, original_code: code }
            });
          }
        }
        if (typeof window.psShowToast === 'function') {
          window.psShowToast('💎 +' + tracked.cost_pts + ' pts restitués (' + tracked.reward_name + ')', 'success');
        }
        // Clear le sessionStorage shipping si c'est ce code (format JSON {code,label}
        // depuis v11, ou string brute legacy — on parse les deux).
        try {
          const raw = sessionStorage.getItem('ps_applied_shipping_code');
          if (raw) {
            let storedCode = raw;
            if (raw.charAt(0) === '{') {
              try { storedCode = (JSON.parse(raw) || {}).code; } catch (_) {}
            }
            if (storedCode === code) {
              sessionStorage.removeItem('ps_applied_shipping_code');
            }
          }
        } catch (_) {}
        // Backend refund (async, fire & forget) : delete Shopify + ledger + cancel
        // + rebuild master code pour les siblings restants.
        fetch(supabaseUrl + '/functions/v1/refund-reward-redemption', {
          method: 'POST',
          headers: { 'apikey': supabaseKey, 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
          body: JSON.stringify({ cid: Number(cid), redemption_id: tracked.redemption_id })
        }).catch(function (err) { console.error('[PS Refund]', err); });
        // Cleanup cart Shopify : POST /cart/update.js {discount:""} vide VRAIMENT tous les
        // codes du panier (le seul vrai clear ; `/discount/none` ne fait rien), puis on
        // re-apply les codes restants pour les autres rewards actifs.
        try {
          await fetch('/cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin', body: JSON.stringify({ discount: '' }),
          });
        } catch (_) {}
        // Re-apply les codes reward restants (autres tracking entries) en chain
        // pour qu'ils reprennent leur place dans la session apres le clear.
        try {
          const remaining = JSON.parse(localStorage.getItem(codesKey) || '{"codes":[]}').codes || [];
          // Inclure aussi le master code restant pour les product_free encore actifs
          // -> on le lit depuis le tracking ps_tracked_rewards_ (product code partage)
          const productTracking = JSON.parse(localStorage.getItem('ps_tracked_rewards_' + cid) || '{"rewards":[]}').rewards || [];
          const remainingCodes = [];
          remaining.forEach(function (r) { if (r.code) remainingCodes.push(r.code); });
          productTracking.forEach(function (r) { if (r.code && remainingCodes.indexOf(r.code) === -1) remainingCodes.push(r.code); });
          if (remainingCodes.length > 0) {
            const chain = remainingCodes.reduceRight(function (next, c) {
              return '/discount/' + encodeURIComponent(c) + '?redirect=' + encodeURIComponent(next);
            }, '/cart.js');
            await fetch(chain, { credentials: 'same-origin', redirect: 'follow' });
          }
        } catch (e) { console.warn('reapply remaining codes failed', e); }
        // Refresh drawer (pas de reload page)
        await refresh();
        return;
      }
    }

    // Code promo AUTO/manuel (VIP-5%, port offert, code tapé…). Shopify n'a PAS de « retirer UN
    // code » : on EFFACE tout (POST /cart/update.js {discount:""}, le seul vrai clear) puis on
    // REPOSE les codes GARDÉS (via /discount/). Le code retiré est mémorisé (opt-out) pour ne
    // pas revenir. Ça tient jusqu'au CHECKOUT (contrairement à /discount/none qui ne faisait rien).
    let cartNow = lastCart;
    if (!cartNow || !cartNow.token) { try { cartNow = await fetchCart(); lastCart = cartNow; } catch (e) {} }
    noterRetire(code);
    // On oublie le flag du port ; il sera reposé ci-dessous SEULEMENT s'il fait partie des gardés.
    try { sessionStorage.removeItem('ps_applied_shipping_code'); } catch (e) {}
    // Codes à GARDER = codes voulus du client MOINS les retirés (dont celui qu'on vient de retirer).
    const garder = [];
    try {
      const cardEl = document.querySelector('.ps-tier-card[data-ps-customer-id]');
      if (cardEl && cardEl.dataset.psSupabaseUrl && cardEl.dataset.psSupabaseKey && cardEl.dataset.psCustomerId) {
        const dd = await fetchClientCodes(cardEl.dataset.psSupabaseUrl, cardEl.dataset.psSupabaseKey, cardEl.dataset.psCustomerId);
        const codes2 = (dd && dd.discount_codes) || [];
        const ret = lireRetires();
        const oc = codes2.filter(function (c) { return c && c.code && c.applies_to !== 'shipping' && ret.indexOf(c.code.toUpperCase()) === -1; });
        const sc = codes2.filter(function (c) { return c && c.code && c.applies_to === 'shipping' && ret.indexOf(c.code.toUpperCase()) === -1; });
        oc.sort(function (a, b) { return Number(b.value || 0) - Number(a.value || 0); });
        if (oc[0]) garder.push(oc[0].code);
        if (sc[0]) { garder.push(sc[0].code); try { sessionStorage.setItem('ps_applied_shipping_code', JSON.stringify({ code: sc[0].code, label: 'Frais de port offerts' })); } catch (e) {} }
      }
    } catch (e) {}
    // 1) EFFACER TOUS les codes — le VRAI clear = POST /cart/update.js {discount:""}.
    //    ⚠️ `/discount/none` NE FAIT RIEN sur des codes permanents (vérifié 22/08 : total_discount
    //    inchangé) → les codes « retirés » revenaient au checkout. /cart/update.js efface bien
    //    la totalité des codes du panier (vérifié : 2 codes -> 0).
    try {
      await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ discount: '' }),
      });
    } catch (e) {}
    // 2) REPOSER les codes gardés (l'application via /discount/, elle, marche).
    if (garder.length) {
      const chaine = garder.reduceRight(function (next, c) {
        return '/discount/' + encodeURIComponent(c) + '?redirect=' + encodeURIComponent(next);
      }, '/cart.js');
      try { await fetch(chaine, { credentials: 'same-origin', redirect: 'follow' }); } catch (e) {}
    }
    // 3) Rafraîchir l'UI (l'état des codes est déjà réglé côté serveur, awaité).
    if (window.location.pathname === '/cart') {
      try { if (cartNow && cartNow.token) sessionStorage.removeItem('ps_cartpage_autodiscount_' + cartNow.token); } catch (e) {}
      window.location.reload();
      return;
    }
    try {
      const fresh = await fetchCart();
      lastCart = fresh;
      renderCart(fresh);
      document.dispatchEvent(new CustomEvent('ps:cart:updated', { detail: { cart: fresh } }));
    } catch (e) {}
  });

  // Auto-open drawer after discount redirect (re-open if flag set)
  if (typeof sessionStorage !== 'undefined') {
    try {
      if (sessionStorage.getItem('ps_cart_reopen') === '1') {
        sessionStorage.removeItem('ps_cart_reopen');
        // Defer open until DOM ready + drawer present
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => setTimeout(open, 100));
        } else {
          setTimeout(open, 100);
        }
      }
    } catch (e) {}
  }

  // ─── Global Quick-Add handler ───
  // Intercepts ALL <form data-ps-quick-add> submits across the site
  // (collection cards, preorders slider, novelties slider, etc.) and adds
  // the variant to cart WITHOUT opening the drawer — just bumps the badge.
  // Button feedback : spinner during POST, "Ajouté ✓" 1.5s then back to price.
  document.addEventListener('submit', async function (e) {
    const form = e.target.closest('[data-ps-quick-add], form.ps-card__variant-form, form.ps-card__form');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[data-ps-add-btn]') || form.querySelector('button[type="submit"]');
    if (!btn || btn.dataset.state === 'loading' || btn.disabled) return;
    btn.dataset.state = 'loading';
    try {
      const fd = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'HTTP ' + res.status);
      }
      await refresh();
      btn.dataset.state = 'added';
      setTimeout(function () { btn.removeAttribute('data-state'); }, 1500);
    } catch (err) {
      console.error('[PS QuickAdd]', err);
      btn.removeAttribute('data-state');
    }
  });
})();
